import { useState } from 'react'

// Inline score-cell controls shared by BOTH scoreboards: the in-meeting
// SectionScoreboard and the team Scoreboard page (task #596). They were
// extracted verbatim from SectionScoreboard so the two grids look and
// behave identically — a click puts the cell straight into edit mode, and
// Enter is handled by the parent (flush the save, advance a row).

export function MetricValueInput({
  unit,
  value,
  formattedValue,
  onTarget,
  isSaving,
  onChange,
  onEnter,
  onBlurred,
  inputRef,
  ariaLabel,
}: {
  unit: string
  value: number | null
  formattedValue: string
  onTarget: boolean | null
  isSaving: boolean
  onChange: (raw: string) => void
  onEnter?: (el: HTMLInputElement) => void
  // Leaving the cell flushes its debounced save immediately, so a pending
  // edit can't still be waiting on a timer when the user navigates away.
  onBlurred?: () => void
  inputRef?: (el: HTMLInputElement | null) => void
  ariaLabel?: string
}) {
  const borderColor = onTarget === true ? 'rgba(21,83,46,0.3)' : onTarget === false ? 'rgba(237,28,36,0.3)' : '#E6E6E6'
  const textColor = onTarget === true ? '#15532E' : onTarget === false ? '#ED1C24' : '#1B1B1B'
  const bgColor = onTarget === true ? 'rgba(21,83,46,0.06)' : onTarget === false ? 'rgba(237,28,36,0.06)' : '#fff'

  // Display/edit toggle (task #565): while blurred the input shows the
  // metric-formatted value ($, commas, K/M — same formatter as Target/YTD);
  // on focus it flips to a raw number input for typing. While focused a
  // local draft string is the source of truth, so a heartbeat prop refresh
  // mid-edit can't clobber or reformat what the user is typing; the parent
  // still receives every raw change (optimistic update + debounced save).
  const [focused, setFocused] = useState(false)
  const [draft, setDraft] = useState('')

  const displayValue = focused
    ? draft
    : (value !== null && value !== undefined ? formattedValue : '')

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
      {focused && unit === '$' && <span style={{ color: '#1B1B1B', fontSize: '14px' }}>$</span>}
      <input
        ref={inputRef}
        type={focused ? 'number' : 'text'}
        step="any"
        value={displayValue}
        aria-label={ariaLabel}
        onChange={(e) => {
          setDraft(e.target.value)
          onChange(e.target.value)
        }}
        placeholder="—"
        readOnly={!focused}
        style={{
          width: '90px',
          padding: '6px 8px',
          border: `2px solid ${borderColor}`,
          borderRadius: '6px',
          fontFamily: 'Montserrat, sans-serif',
          fontSize: '15px',
          fontWeight: 700,
          color: textColor,
          backgroundColor: bgColor,
          textAlign: 'right',
          outline: 'none',
          cursor: focused ? 'text' : 'pointer',
          transition: 'border-color 0.2s ease',
        }}
        onFocus={(e) => {
          setDraft(value !== null && value !== undefined ? String(value) : '')
          setFocused(true)
          e.target.style.borderColor = '#351E1D'
        }}
        onBlur={(e) => {
          setFocused(false)
          e.target.style.borderColor = borderColor
          onBlurred?.()
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            // onEnter flushes the save and handles focus itself (moving to
            // the next row's input, or blurring when there is no next row).
            onEnter?.(e.currentTarget)
          }
        }}
      />
      {focused && unit === '%' && <span style={{ color: '#1B1B1B', fontSize: '14px' }}>%</span>}
      {isSaving && (
        <span style={{ fontSize: '11px', color: '#1B1B1B', fontWeight: 600 }}>saving...</span>
      )}
    </div>
  )
}

export function WonLostToggle({
  value,
  manual,
  onChange,
  isSaving,
  compact,
}: {
  value: boolean | null
  manual: boolean
  onChange: (v: boolean) => void
  isSaving: boolean
  compact?: boolean
}) {
  // Hover/focus tooltip (native title) instead of always-visible text —
  // keeps rows compact now that the toggle sits under each value cell.
  const hint = value === null
    ? undefined
    : manual
      ? 'Manual override · click again to auto-calculate'
      : 'Auto-calculated · click to override'
  const btnFontSize = compact ? '10px' : '12px'
  const btnPadding = compact ? '3px 8px' : '5px 12px'

  return (
    <div title={hint} style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
      <div style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
        <button
          onClick={() => onChange(true)}
          aria-pressed={value === true}
          disabled={isSaving}
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: btnFontSize,
            fontWeight: 700,
            padding: btnPadding,
            borderRadius: '5px',
            border: 'none',
            cursor: isSaving ? 'wait' : 'pointer',
            transition: 'all 0.15s ease',
            backgroundColor: value === true ? '#15532E' : '#F5F5F5',
            color: value === true ? '#fff' : '#1B1B1B',
          }}
        >
          WON
        </button>
        <button
          onClick={() => onChange(false)}
          aria-pressed={value === false}
          disabled={isSaving}
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: btnFontSize,
            fontWeight: 700,
            padding: btnPadding,
            borderRadius: '5px',
            border: 'none',
            cursor: isSaving ? 'wait' : 'pointer',
            transition: 'all 0.15s ease',
            backgroundColor: value === false ? '#ED1C24' : '#F5F5F5',
            color: value === false ? '#fff' : '#1B1B1B',
          }}
        >
          LOST
        </button>
      </div>
    </div>
  )
}
